import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { BusinessLinesSelect } from '@components/BusinessLinesSelect';
import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { languageOptions } from '@constants/languages.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import {
  createPartnershipSchema,
  type CreatePartnershipPayload,
} from '@services/partnerships/dtos/create.dto';
import { partnershipService } from '@services/partnerships/service';
import type { CreatePartnershipOutput } from '@services/partnerships/types/create.type';

import { PostcodesField } from './@components/Postcodes/Field';
import { ServicesField } from './@components/Services/Field';

export const Route = createFileRoute(
  '/_layout/(private)/providers/$id/partnerships/create',
)({
  component: ProviderPartnershipCreate,
});

function ProviderPartnershipCreate() {
  const { id: providerId } = Route.useParams();

  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CreatePartnershipPayload>({
    resolver: zodResolver(createPartnershipSchema),
    mode: 'onBlur',
    defaultValues: {
      acceptsVipRequests: false,
      businessLineId: '',
      coveredLanguages: [],
      coveredPostcodes: [],
      name: '',
      providerId,
      services: [],
    },
  });

  const { isPending, mutate: createPartnership } = useMutation<
    CreatePartnershipOutput,
    unknown,
    CreatePartnershipPayload
  >({
    mutationFn: partnershipService.create,
    onError: handleRequestError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partnerships.all });

      invalidateSmartEventTimeline({
        id: providerId,
        queryClient,
        type: ESmartEventTimelineKeyType.Provider,
      });
      navigate({
        to: '/providers/$id',
        params: { id: providerId },
        replace: true,
      });
      toast.success('Success', {
        description: 'Partnership created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createPartnership({ ...values, providerId });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: providerId }}
        title="Create partnership"
        to="/providers/$id"
      />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-2">
          <InputControlled label="Name" name="name" />

          <BusinessLinesSelect />
        </div>

        <ServicesField />
        <PostcodesField />

        <Separator variant="form" />

        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-3">
          <InputControlled
            label="Covered languages"
            name="coveredLanguages"
            options={languageOptions}
            variant="checkboxGroup"
          />
          <InputControlled
            name="acceptsVipRequests"
            title="Accepts VIP requests"
            variant="switch"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id: providerId }}
            rounded="lg"
            size="lg"
            to="/providers/$id"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
